import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCrsfToken } from '~/hooks/useCrsfToken'
import { MfaEnrolment, MfaError, MfaStatus } from '../types'

const jsonHeaders = (csrfToken: string) => ({
    'Content-Type': 'application/json',
    'X-CSRFToken': csrfToken,
})

/** Turn a non-2xx response into the shape the forms render. */
const throwMfaError = async (resp: Response): Promise<never> => {
    let message = 'Something went wrong. Please try again.'
    try {
        const body = await resp.json()
        if (body?.error) message = body.error
    } catch (e) {
        /* non-JSON error page; keep the generic message */
    }
    throw { message, status: resp.status } as MfaError
}

/** What the signed-in user still needs to do about two-factor. */
export const useMfaStatus = (enabled = true) =>
    useQuery<MfaStatus>({
        queryKey: ['mfa-status'],
        queryFn: async () => {
            const resp = await fetch('/api/account/mfa/status')
            if (!resp.ok) return throwMfaError(resp)
            return resp.json()
        },
        enabled,
        retry: false,
    })

/**
 * Mint a new (unconfirmed) secret and its QR.
 *
 * A mutation rather than a query on purpose: each call replaces the pending
 * secret server-side, so it must never fire on a refetch or a remount.
 */
export const useMfaSetup = () => {
    const csrfToken = useCrsfToken()

    return useMutation<MfaEnrolment, MfaError, void>({
        mutationFn: async () => {
            const resp = await fetch('/api/account/mfa/setup', {
                headers: jsonHeaders(csrfToken),
            })
            if (!resp.ok) return throwMfaError(resp)
            return resp.json()
        },
    })
}

/** Confirm enrolment with a live code; resolves to the one-time recovery codes. */
export const useMfaConfirm = () => {
    const csrfToken = useCrsfToken()
    const queryClient = useQueryClient()

    return useMutation<{ recovery_codes: string[] }, MfaError, { code: string }>({
        mutationFn: async ({ code }) => {
            const resp = await fetch('/api/account/mfa/setup', {
                method: 'POST',
                headers: jsonHeaders(csrfToken),
                body: JSON.stringify({ code }),
            })
            if (!resp.ok) return throwMfaError(resp)
            return resp.json()
        },
        // Deliberately does NOT invalidate 'mfa-status' here. Doing so made the
        // parent page refetch, see session_verified flip to true, switch branch,
        // and unmount the enrolment component -- destroying the recovery codes it
        // was about to display. They are shown once and cannot be retrieved, so
        // the refresh waits until the user confirms they have saved them.
        onSuccess: () => {
            queryClient.invalidateQueries(['viewer'])
        },
    })
}

/**
 * Complete a pending login challenge.
 *
 * Unauthenticated: the session is parked between password and second factor, so
 * there is no viewer yet -- which is exactly why success has to invalidate it.
 */
export const useMfaVerify = () => {
    const csrfToken = useCrsfToken()
    const queryClient = useQueryClient()

    return useMutation<unknown, MfaError, { code: string }>({
        mutationFn: async ({ code }) => {
            const resp = await fetch('/api/account/mfa/verify', {
                method: 'POST',
                headers: jsonHeaders(csrfToken),
                body: JSON.stringify({ code }),
            })
            if (!resp.ok) return throwMfaError(resp)
            return resp.json()
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['viewer'])
            queryClient.invalidateQueries(['mfa-status'])
        },
    })
}

/** Replace the recovery codes. Requires a session that already passed 2FA. */
export const useMfaRecoveryCodes = () => {
    const csrfToken = useCrsfToken()
    const queryClient = useQueryClient()

    return useMutation<{ recovery_codes: string[] }, MfaError, void>({
        mutationFn: async () => {
            const resp = await fetch('/api/account/mfa/recovery-codes', {
                method: 'POST',
                headers: jsonHeaders(csrfToken),
            })
            if (!resp.ok) return throwMfaError(resp)
            return resp.json()
        },
        onSuccess: () => queryClient.invalidateQueries(['mfa-status']),
    })
}
